import React, { useMemo } from 'react';
import {
    IconClipboardData,
    IconFileAlert,
    IconHome,
    IconStack2,
    IconUpload,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client';

import { useAuth } from '../../context/AuthProvider';
import { useUser } from '../../hooks/useUser';
import { GET_MASTER_DATA_IMPORT_ERRORS } from '../../apollo/queries/masterDataImportErrors';
import {
    getIsPricingBatchesModuleEnabled,
    getIsPriceChangeModuleEnabled,
} from '../../constants/navigation';
import { checkUserRole } from '../../utility/userRoles';
import { RESTRICTED_SKU_UPLOAD_ROLES } from '../../constants/restrictedSkuUploadAccess';
import Sidebar from './Sidebar';
import { ModuleLink } from './SidebarItem';
import { useAppConfig } from '../../hooks/useAppConfig';

const moduleLinks: ModuleLink[] = [
    {
        label: 'Home',
        icon: IconHome,
        path: '',
    },
    {
        label: 'Request & Approvals',
        icon: IconClipboardData,
        path: 'requests',
    },
    {
        label: 'Pricing batches',
        icon: IconStack2,
        path: 'pricing-batches',
    },
    {
        label: 'Errors',
        icon: IconFileAlert,
        path: 'master-data-import-errors',
    },
    {
        label: 'Upload',
        icon: IconUpload,
        path: 'upload',
    },
];

const allowedDesignations = [
    'senior procurement manager',
    'procurement executive',
];

function PriceChangeSidebar() {
    const { authUser, role } = useAuth();
    const { isStoreManager } = useUser();
    const { pricingBatchesModuleEnabled } = useAppConfig();
    const isPriceChangeModuleEnabled = getIsPriceChangeModuleEnabled(
        authUser,
        isStoreManager,
    );

    const { data: masterDataImportErrors } = useQuery(
        GET_MASTER_DATA_IMPORT_ERRORS,
    );

    const hasErrors = useMemo(() => {
        if (!masterDataImportErrors) {
            return false;
        }
        return (
            masterDataImportErrors.getMasterDataErrors.operationHierarchyErrors
                .length > 0 ||
            masterDataImportErrors.getMasterDataErrors
                .procurementHierarchyErrors.length > 0 ||
            masterDataImportErrors.getMasterDataErrors.productImportErrors
                .length > 0
        );
    }, [masterDataImportErrors]);

    const filteredManagementLinks = useMemo(
        () =>
            moduleLinks
                .filter(link => {
                    if (isStoreManager) {
                        return link.label === 'Request & Approvals';
                    }
                    if (link.label === 'Pricing batches') {
                        return getIsPricingBatchesModuleEnabled(
                            authUser,
                            role,
                            pricingBatchesModuleEnabled,
                        );
                    }
                    if (link.label === 'Errors') {
                        return (
                            hasErrors &&
                            (allowedDesignations.includes(
                                authUser?.designation ?? '',
                            ) ||
                                role === 'admin')
                        );
                    }
                    if (link.label === 'Upload') {
                        return checkUserRole(
                            authUser,
                            RESTRICTED_SKU_UPLOAD_ROLES,
                        );
                    }
                    return true;
                })
                .map(link => ({
                    ...link,
                    showIndicator: link.label === 'Errors' && hasErrors,
                })),
        [
            hasErrors,
            authUser,
            role,
            pricingBatchesModuleEnabled,
            isStoreManager,
        ],
    );

    if (isStoreManager && !isPriceChangeModuleEnabled) {
        return null;
    }

    return (
        <Sidebar
            moduleLinks={filteredManagementLinks}
            pathPrefix="/price-change/"
        />
    );
}

export default PriceChangeSidebar;
